import {
    array,
    boolean,
    enum as zodEnum,
    literal,
    number,
    object,
    string,
    TypeOf,
    union,
} from 'zod';

import { RenewalPeriod } from './subscription';

/* --------------------------- SubscriptionStatus --------------------------- */

const SubscriptionStatus = zodEnum([
    'active',
    'cancelled',
    'unpaid',
]);
export type SubscriptionStatus = TypeOf<typeof SubscriptionStatus>;

/* ---------------------------- BaseSubscription ---------------------------- */

export const BaseSubscription = object({
    id: string(),
    managed: boolean(),
    name: string(),
    status: SubscriptionStatus,
});
export type BaseSubscription = TypeOf<typeof BaseSubscription>;

/* -------------------------- UnmanagedSubscription ------------------------- */

const UnmanagedSubscription = BaseSubscription.extend({
    managed: literal(false),
});
export type UnmanagedSubscription = TypeOf<typeof UnmanagedSubscription>;

/* --------------------------- ManagedSubscription -------------------------- */

export const BaseManagedSubscription = BaseSubscription.extend({
    managed: literal(true),

    cancels: string().optional(),
    currency: string().optional(),
    image: string().optional(),
    interval: RenewalPeriod,
    nextPaymentAttempt: string().optional(),
    paymentFailed: string().optional(),
    provider: string(),
    renews: string().optional(),
    value: number().optional(),
});
export type BaseManagedSubscription = TypeOf<typeof BaseManagedSubscription>;

/* ---------------------------- TierSubscription ---------------------------- */

const TierSubscription = BaseManagedSubscription.extend({
    tierId: string(),
});
export type TierSubscription = TypeOf<typeof TierSubscription>;

/* --------------------------- BundleSubscription --------------------------- */

const BundleSubscription = BaseManagedSubscription.extend({
    bundleId: string(),
});
export type BundleSubscription = TypeOf<typeof BundleSubscription>;

/* --------------------------- ManagedSubscription -------------------------- */

export const ManagedSubscription = union([
    TierSubscription,
    BundleSubscription,
]);
export type ManagedSubscription = TypeOf<typeof ManagedSubscription>;

/* ---------------------------- UserSubscription ---------------------------- */

export const UserSubscription = union([
    UnmanagedSubscription,
    TierSubscription,
    BundleSubscription,
]);
export type UserSubscription = TypeOf<typeof UserSubscription>;

/* ---------------------------------- User ---------------------------------- */

export const User = object({
    id: string(),
    created: string(),
    email: string(),
    emailVerified: boolean(),
    firstName: string().optional(),
    lastName: string().optional(),
    postCode: string().optional(),
    gender: string().optional(),
    yearOfBirth: number().optional(),
    marketingOptIn: boolean().optional(),
    extend: object({
        subscriptions: array(UserSubscription).optional(),
    }).optional(),
});
export type User = TypeOf<typeof User>;
